import { Tag } from '@components';
import { Sync } from '@mui/icons-material';
import { Box, Divider, type SxProps, type Theme, Tooltip, useTheme } from '@mui/material';
import { getIn, useFormikContext } from 'formik';
import { type FC, useEffect, useMemo, useRef } from 'react';

import type { Currency, CurrencyWireFormat } from '@core/types/api/Currency/Currency';
import type Enums from '@core/types/api/Enums';
import type { QuoteProductDiscount } from '@core/types/api/QuoteProductDiscount/QuoteProductDiscount';
import { cleanedDecimal, formatDecimalString } from '@core/utils/decimals';
import {
  reappliedAtGross,
  resolveDiscountsAtGross,
  resolveDiscountsAtNet,
  savedPercentShares,
  savedPercentSharesOfRows,
  settledNetPrice,
} from '@core/utils/discounts';
import { hasStringValue, hasValue } from '@core/utils/equality';
import { AppliedTierType, priceSourceLabel, tierPriceStatusLabel } from '@core/utils/tierPricing';
import { useMatchingDiscounts } from '@web/hooks/useMatchingDiscounts';

import { Input, type InputProps } from '../../Input/Input';

type Props = Omit<InputProps, 'type' | 'startAdornment' | 'onInputChange'> & {
  tierPrice: Currency | undefined | null;
  tierPriceType: AppliedTierType;
  originalPrice?: CurrencyWireFormat | null;
  onPriceChange: (unitPriceNumber: string, discounts?: QuoteProductDiscount[]) => void;
  currencySymbol?: string;
  discounts?: readonly QuoteProductDiscount[];
  grossPrice?: number;
  discountTotal?: number;
  discountCount?: number;
  companyID?: string | null;
  productID?: string;
  deliveryType?: Enums.ProductDeliveryType | null;
};

export const UnitPrice: FC<Props> = ({
  tierPrice,
  tierPriceType,
  originalPrice = null,
  name,
  onPriceChange,
  currencySymbol = '$',
  discounts = [],
  grossPrice = 0,
  discountTotal = 0,
  discountCount = 0,
  companyID = null,
  productID = '',
  deliveryType = null,
  disabled,
  readOnly,
  ...rest
}) => {
  const theme = useTheme();
  const {
    discounts: matchingDiscounts,
    isSuccess: hasMatchingDiscounts,
    isError: failedMatchingDiscounts,
  } = useMatchingDiscounts({
    companyID,
    productID,
    deliveryType,
    enabled: discountCount > 0,
  });
  const awaitsMatchingDiscounts = discountCount > 0 && !hasMatchingDiscounts;
  const { values, initialValues } = useFormikContext();
  const fieldNumberValue = getIn(values, `${name}.number`) as string | undefined;
  const initialRows = getIn(initialValues, name.replace(/\.\d+\.price$/, '')) as unknown;
  const initialShares = useMemo(
    () =>
      savedPercentSharesOfRows(
        Array.isArray(initialRows)
          ? (initialRows as {
              price?: CurrencyWireFormat | null;
              discounts?: QuoteProductDiscount[];
            }[])
          : [],
      ),
    [initialRows],
  );
  const savedSharesRef = useRef(initialShares);

  const isStoredPriceNegative =
    hasStringValue(fieldNumberValue) && parseFloat(cleanedDecimal(fieldNumberValue)) < 0;

  const showFixedPricing =
    hasValue(tierPrice) &&
    tierPriceType &&
    tierPriceType !== AppliedTierType.None &&
    tierPrice.number !== '';

  const tierGrossPrice = hasValue(tierPrice) ? parseFloat(cleanedDecimal(tierPrice.number)) : 0;
  // A re-priced line re-resolves against the current offers, so every derived price starts from
  // the same selection the picker would settle on; an untouched line keeps its saved deal.
  const resolvedAtTier = hasMatchingDiscounts
    ? resolveDiscountsAtGross(
        discounts,
        matchingDiscounts,
        tierGrossPrice,
        grossPrice,
        savedSharesRef.current,
      )
    : {
        discounts: reappliedAtGross(
          discounts,
          matchingDiscounts,
          tierGrossPrice,
          grossPrice,
          savedSharesRef.current,
        ),
        belowZero: [],
      };
  const reappliedAtTier = resolvedAtTier.discounts;
  const netTierPrice = settledNetPrice(
    [...reappliedAtTier, ...resolvedAtTier.belowZero],
    tierGrossPrice,
  );
  const isTierPricingMatch =
    showFixedPricing &&
    hasStringValue(fieldNumberValue) &&
    Math.abs(grossPrice - tierGrossPrice) < 0.00005;

  const isAtBasePrice =
    !showFixedPricing &&
    hasValue(originalPrice) &&
    hasStringValue(originalPrice.number) &&
    Math.abs(grossPrice - parseFloat(cleanedDecimal(originalPrice.number))) < 0.00005;

  const circleColor = isTierPricingMatch
    ? theme.palette.SlabGreen[600]
    : theme.palette.SlabRed[600];

  const withDiscounts = discountCount > 0 ? ' less discounts' : '';
  const tierStatus = showFixedPricing
    ? tierPriceStatusLabel({
        appliedTierType: tierPriceType,
        isAtTierPrice: isTierPricingMatch === true,
        netUnitPrice: parseFloat(cleanedDecimal(fieldNumberValue ?? '')) || 0,
        tierNetPrice: netTierPrice,
        hasDiscounts: discountCount > 0,
        currencySymbol,
      })
    : '';

  const discountTooltip = `${discountCount} ${
    discountCount === 1 ? 'discount' : 'discounts'
  } applied, ${currencySymbol}${discountTotal.toFixed(2)} off`;

  const detailsRowSx: SxProps<Theme> = { display: 'flex', alignItems: 'center', gap: '0.5rem' };
  const breakdownRowSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };
  const popupDotSx = (color: string): SxProps<Theme> => ({
    flexShrink: 0,
    width: '0.875rem',
    height: '0.875rem',
    borderRadius: '50%',
    backgroundColor: color,
  });
  const indicatorDotSx = (color: string): SxProps<Theme> => ({
    width: '0.875rem',
    height: '0.875rem',
    borderRadius: '50%',
    backgroundColor: color,
  });

  const latestDiscountsRef = useRef({ discounts, matchingDiscounts, grossPrice });
  useEffect(() => {
    latestDiscountsRef.current = { discounts, matchingDiscounts, grossPrice };
  }, [discounts, matchingDiscounts, grossPrice]);
  useEffect(() => {
    if (grossPrice > 0) {
      savedSharesRef.current = new Map([
        ...savedPercentShares(discounts, grossPrice - discountTotal),
        ...initialShares,
      ]);
    }
  }, [discounts, grossPrice, discountTotal, initialShares]);

  const priceChange = (unitPriceNumber: string): void => {
    const latest = latestDiscountsRef.current;
    if (discountCount === 0) {
      onPriceChange(unitPriceNumber);
      return;
    }
    const netUnitPrice = parseFloat(cleanedDecimal(unitPriceNumber)) || 0;
    onPriceChange(
      unitPriceNumber,
      resolveDiscountsAtNet(
        latest.discounts,
        latest.matchingDiscounts,
        netUnitPrice,
        latest.grossPrice,
        savedSharesRef.current,
      ),
    );
  };

  const isResetBlocked =
    awaitsMatchingDiscounts || (discountCount > 0 && resolvedAtTier.belowZero.length > 0);

  const syncClick = (): void => {
    if (hasValue(tierPrice) && !isResetBlocked) {
      const target = settledNetPrice(reappliedAtTier, tierGrossPrice).toFixed(4);
      onPriceChange(
        formatDecimalString(target) || target,
        discountCount > 0 ? reappliedAtTier : undefined,
      );
    }
  };
  const sourceLabel = priceSourceLabel(tierPriceType, true);
  const loadingTooltip = failedMatchingDiscounts
    ? "Can't reset: the line's discounts couldn't be loaded. Reload to retry."
    : "Can't reset: the line's discounts haven't loaded.";
  const blockedTooltip = awaitsMatchingDiscounts
    ? loadingTooltip
    : `Can't reset: discounts exceed ${sourceLabel} price. Remove a discount first.`;
  const syncTooltip = isResetBlocked
    ? blockedTooltip
    : `Reset to ${sourceLabel} pricing${withDiscounts}`;
  const sync =
    showFixedPricing && !isTierPricingMatch && readOnly !== true ? (
      <Tooltip title={syncTooltip}>
        <Sync
          sx={{
            cursor: isResetBlocked ? 'not-allowed' : 'pointer',
            color: isResetBlocked ? theme.palette.SlabGray[400] : undefined,
          }}
          onClick={syncClick}
        />
      </Tooltip>
    ) : undefined;

  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Input
        {...rest}
        name={name}
        type='currency'
        allowNegative={isStoredPriceNegative}
        disabled={disabled === true || awaitsMatchingDiscounts}
        readOnly={readOnly}
        startAdornment={currencySymbol}
        endAdornment={sync}
        onInputChange={priceChange}
      />
      {(showFixedPricing || discountCount > 0) && (
        <Tooltip
          title={
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                {showFixedPricing && (
                  <Box sx={detailsRowSx}>
                    <Box sx={popupDotSx(circleColor)} />
                    <span>{tierStatus}</span>
                  </Box>
                )}
                {discountCount > 0 && (
                  <Box sx={detailsRowSx}>
                    <Box sx={popupDotSx(theme.palette.SlabYellow[800])} />
                    <span>{discountTooltip}</span>
                  </Box>
                )}
              </Box>
              {discountCount > 0 && (
                <>
                  <Divider sx={{ borderColor: theme.palette.SlabGray.Stroke }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                    <Box sx={breakdownRowSx}>
                      <Box sx={detailsRowSx}>
                        <Box component='span' sx={{ color: theme.palette.SlabGray[600] }}>
                          Gross
                        </Box>
                        <Tag
                          variant='Gray'
                          size='sm'
                          label={priceSourceLabel(
                            tierPriceType,
                            isTierPricingMatch === true,
                            isAtBasePrice,
                          )}
                        />
                      </Box>
                      <span>{`${currencySymbol}${grossPrice.toFixed(2)}`}</span>
                    </Box>
                    {discounts.map((discount) => (
                      <Box key={discount.importedDiscountId} sx={breakdownRowSx}>
                        <Box component='span' sx={{ color: theme.palette.SlabGray[600] }}>
                          {discount.name}
                        </Box>
                        <span>
                          {`-${currencySymbol}${parseFloat(discount.appliedAmount || '0').toFixed(2)}`}
                        </span>
                      </Box>
                    ))}
                  </Box>
                  <Divider sx={{ borderColor: theme.palette.SlabGray.Stroke }} />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontSize: '1.125rem',
                      fontWeight: 500,
                      color: theme.palette.SlabGray[950],
                    }}
                  >
                    <span>Net</span>
                    <span>{`${currencySymbol}${(grossPrice - discountTotal).toFixed(2)}`}</span>
                  </Box>
                </>
              )}
            </Box>
          }
          slotProps={{
            tooltip: {
              sx: {
                backgroundColor: theme.palette.SlabGray[0],
                color: theme.palette.SlabGray[900],
                border: `1px solid ${theme.palette.SlabGray.Stroke}`,
                borderRadius: '0.5rem',
                boxShadow: theme.shadows[8],
                padding: '0.75rem',
                fontSize: '1rem',
                fontWeight: 400,
                lineHeight: '1.5rem',
                maxWidth: '25rem',
                minWidth: discountCount > 0 ? '25rem' : undefined,
              },
            },
          }}
        >
          <Box
            role='img'
            aria-label={[tierStatus, discountCount > 0 ? discountTooltip : '']
              .filter(hasStringValue)
              .join('. ')}
            sx={{
              position: 'absolute',
              right: '0.5rem',
              top: 0,
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '0.5rem',
              cursor: 'pointer',
            }}
          >
            {discountCount > 0 && <Box sx={indicatorDotSx(theme.palette.SlabYellow[800])} />}
            {showFixedPricing && <Box sx={indicatorDotSx(circleColor)} />}
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};
